import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { categoriesApi } from '@/shared/api/categories';
import { CreateTicketForm } from '@/features/tickets';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import type { Category, Subcategory, SubcategoryMaterial } from '@/entities/category';
import { CatalogIcon } from '@/shared/config';
import { CategoryGlyph } from '@/widgets/CatalogTile';
import { isSafeMaterialUrl } from '@/pages/AdminCategories/materials';
import { Card, EmptyState, PageMotion, Skeleton } from '@/shared/ui';
import styles from './SubcategoryDetail.module.css';

function hostOf(url: string): string {
  try {
    return new URL(url, window.location.origin).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export const SubcategoryDetailPage: React.FC = () => {
  const { categoryId, subcategoryId } = useParams<{ categoryId: string; subcategoryId: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<Category | null>(null);
  const [subcategory, setSubcategory] = useState<Subcategory | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!categoryId || !subcategoryId) return;
    Promise.all([
      categoriesApi.list().then((r) => {
        const raw = r.data;
        const list = Array.isArray(raw) ? raw : (raw as { data?: Category[] })?.data ?? [];
        return list.find((c) => c.id === categoryId) ?? null;
      }),
      categoriesApi.getSubcategory(subcategoryId).then((r) => {
        const raw = r.data;
        return (raw as Subcategory) ?? null;
      }),
    ])
      .then(([cat, sub]) => {
        setCategory(cat ?? null);
        setSubcategory(sub ?? null);
      })
      .catch(() => {
        setCategory(null);
        setSubcategory(null);
      })
      .finally(() => setLoading(false));
  }, [categoryId, subcategoryId]);

  if (!categoryId || !subcategoryId) return null;

  const materials = subcategory?.materials ?? [];
  const hasMaterials = Array.isArray(materials) && materials.length > 0;

  return (
    <PageMotion>
      <main className={styles.wrapper}>
        { }
        <nav className={styles.breadcrumb} aria-label="Путь в каталоге">
          <Link className={styles.breadcrumbLink} to="/categories">
            <CatalogIcon size={14} aria-hidden />
            <span className={styles.breadcrumbLabel}>Каталог</span>
          </Link>
          <ChevronRight size={14} className={styles.breadcrumbSep} aria-hidden />
          <Link
            className={styles.breadcrumbLink}
            to={`/categories/${categoryId}/subcategories`}
            title={category?.name}
          >
            <span className={styles.breadcrumbLabel}>{category?.name ?? '…'}</span>
          </Link>
        </nav>
        {loading ? (
          <>
            <div className={styles.hero}>
              <Skeleton className={styles.glyphSkeleton} radius="var(--radius-md)" />
              <Skeleton className={styles.titleSkeleton} />
            </div>
            <Skeleton className={styles.formSkeleton} radius="var(--radius-lg)" />
          </>
        ) : !subcategory ? (
          <EmptyState
            Icon={CatalogIcon}
            title="Подкатегория не найдена"
            hint="Возможно, её убрали из каталога. Выберите другую или опишите задачу в свободной форме."
            action={<Link to="/categories">Вернуться в каталог</Link>}
          />
        ) : (
          <>
            <header className={styles.hero}>
              <CategoryGlyph icon={category?.icon} seed={category?.id} />
              <h1 className={styles.pageTitle}>{subcategory.name}</h1>
            </header>
            {subcategory.description && (
              <div className={styles.description}>{subcategory.description}</div>
            )}
            {hasMaterials && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Полезные материалы</h2>
                { }
                <Card padded={false} className={styles.materials}>
                  <ul className={styles.materialsList}>
                    {materials.map((m: SubcategoryMaterial, idx: number) => (
                      <li key={m.id ?? idx}>
                        { }
                        {m.url && isSafeMaterialUrl(m.url) ? (
                          <a
                            className={styles.material}
                            href={m.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span className={styles.materialMain}>
                              <span className={styles.materialTitle}>{m.title}</span>
                              <span className={styles.materialHost} title={m.url}>{hostOf(m.url)}</span>
                            </span>
                            <ArrowUpRight className={styles.materialArrow} size={16} aria-hidden />
                          </a>
                        ) : (
                          <div className={styles.material}>
                            <span className={styles.materialTitle}>{m.title}</span>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </Card>
              </section>
            )}
            <section className={styles.formSection}>
              <CreateTicketForm
                defaultSubcategoryId={subcategoryId}
                onSuccess={() => navigate('/tickets')}
              />
            </section>
          </>
        )}
      </main>
    </PageMotion>
  );
};
